import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { MessageService, LazyLoadEvent } from 'primeng/api';
import { FieldTypeEnum, BackOfficeEnum, RiderVehicleTypeEnum } from 'app/models';
import { AppDataService, ConfigService, OperatorService } from 'app/services';
import { Utils } from 'app/utils';
import * as moment from 'moment';
import { AppComponent } from 'app/app.component';
import {ColumnSelectorComponent} from 'app/column-selector/column-selector.component';
import { OrderUtils } from 'app/utils/order-utils';
import { TableLazyLoadEvent } from 'primeng/table';

@Component({
    selector: 'operators',
    templateUrl: './operators.component.html',
    styleUrls: ['./operators.component.scss',
        '../../../../styles/paginator.scss',
        '../../../../styles/style-layout.scss'],
    providers: [MessageService]
})
export class OperatorsComponent implements OnInit {
    @Input()
    public menuPermissionActive!: string;

    public backOfficeActive!: BackOfficeEnum;
    @Input()
    set _backOfficeActive(value: BackOfficeEnum) {
        this.backOfficeActive = value;
        if (this.backOfficeActive && !this.lazyLoadHandled) {
            this.handleLazyLoad({
                first: 0,
                rows: this.rowsPerPage
            });
        }
        this.title = (value === BackOfficeEnum.Passenger2W) ? 'Passenger 2w Operators' : 'Passenger 4w Operators';
    }

    private lazyLoadHandled = true;

    @ViewChild(ColumnSelectorComponent)
    public columnSelector!: ColumnSelectorComponent;

    constructor(private messageService: MessageService,
        public configService: ConfigService,
        public app: AppComponent,
        public appData: AppDataService,
        public operatorService: OperatorService) { }

    public fieldTypeEnum = FieldTypeEnum;

    public operators!: any;

    public title: string = 'Passenger 4w Operators';

    public loading: boolean = true;
    public exportLoading: boolean = false;

    public operatorHeaderSearch: any = {
        operatorId: null,
        operatorName: null,
        driversCount: null,
        vehiclesCount: null,
        phone: null,
        email: null
    };

    public cols: any[] = [
        {
            field: 'OperatorId', header: 'Operator Id', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.InputNumber, model: 'operatorId', placeholder: 'Type here', active: false
        },
        {
            field: 'OperatorName', header: 'Operator Name', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'operatorName', placeholder: 'Type here', active: false
        },
        {
            field: 'DriversCount', header: 'No. Drivers', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.InputNumber, model: 'driversCount', placeholder: 'Type here', active: false
        },
        {
            field: 'VehiclesCount', header: 'No. Vehicles', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.InputNumber, model: 'vehiclesCount', placeholder: 'Type here', active: false
        },
        {
            field: 'Phone', header: 'Phone No.', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'phone', placeholder: 'Type here', active: false
        },
        {
            field: 'Email', header: 'Email', width: '20%', disableSort: false,
            fieldType: FieldTypeEnum.Inputs, model: 'email', placeholder: 'Type here', active: false
        },
    ];
    public defaultCols = this.cols.map(x => x);

    // Grid settings
    public rowsPerPage: number = 15;
    public totalRecords: number = -1;
    public exportHeaderColumns: string[] = [];
    public exportDataColumns: string[] = [];

    public first: number = 0;
    public sortField: string | string[] = '';
    private sortOrder: number = 1;

    private searchTimer: number = 0;

    public visibleExportOperators: boolean = false;

    ngOnInit(): void { }

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        const skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        if (!event.sortField) {
            this.sortField = 'Id';
            this.sortOrder = -1; // Desc
        }
        else {
            this.sortField = event.sortField;
            this.sortOrder = event.sortOrder ? event.sortOrder : 1;
        }

        this.title = (this.backOfficeActive === BackOfficeEnum.Passenger2W) ? 'Passenger 2w Operators' : 'Passenger 4w Operators';

        if (this.columnSelector) {
            this.columnSelector.loadTableSettings(this.title);
        }

        this.getOperators(skip);
        this.lazyLoadHandled = false;
    }

    public onSelectedColumnsChange(event: any[]) {
        if (event && event.length > 0) {
            this.cols = event;
        } else {
            this.cols = this.defaultCols;
        }

        this.exportHeaderColumns = this.cols.map(x => x.header);
        this.exportDataColumns = this.cols.map(x => x.field);
    }

    private getOperatorsDto(skip: number): any {
        return {
            OperatorId: (this.operatorHeaderSearch.operatorId) ? this.operatorHeaderSearch.operatorId : null,
            OperatorName: (this.operatorHeaderSearch.operatorName) ? this.operatorHeaderSearch.operatorName : null,
            DriversCount: (this.operatorHeaderSearch.driversCount) ? this.operatorHeaderSearch.driversCount : null,
            VehiclesCount: (this.operatorHeaderSearch.vehiclesCount) ? this.operatorHeaderSearch.vehiclesCount : null,
            Phone: (this.operatorHeaderSearch.phone) ? this.operatorHeaderSearch.phone : null,
            Email: (this.operatorHeaderSearch.email) ? this.operatorHeaderSearch.email : null,
            VehicleTypes: this.getVehicleTypeByBackOffice(),
            TimeZoneOffset: Utils.getTimezoneOffset(),
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder
        };
    }

    private getOperators(skip: number) {
        this.loading = true;

        this.operatorService.getOperators(this.getOperatorsDto(0), this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    this.loading = false;
                    return;
                }

                this.operators = data.Items;
                this.totalRecords = data.TotalRecords;
                this.loading = false;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    public handleHeaderFilterChange(event: any) {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.getOperators(0);
            this.searchTimer = 0;
        }, 1500);
    }

    private getVehicleTypeByBackOffice() {
        switch (this.backOfficeActive) {
            case BackOfficeEnum.Passenger2W:
                return [RiderVehicleTypeEnum.Moto];
            case BackOfficeEnum.Passenger4W:
                return [RiderVehicleTypeEnum.Sedan, RiderVehicleTypeEnum.SUV];
            default: return null;
        }
    }

    openExportOperators() {
        this.visibleExportOperators = true;
    }
    changedVisibleExportOperatorsEmit(value: boolean) {
        this.visibleExportOperators = value;
    }

    exportOperatorsEmit(filter: any) {
        this.exportLoading = true;

        const dto = this.getOperatorsDto(0);

        dto.StartDate = filter.startDate ? moment(filter.startDate).format('YYYY-MM-DDTHH:mm:ss') : null;
        dto.EndDate = filter.endDate ? moment(filter.endDate).format('YYYY-MM-DDTHH:mm:ss') : null;;
        dto.FileType = filter.fileType;
        dto.HeaderColumns = this.exportHeaderColumns;
        dto.DataColumns = this.exportDataColumns;

        this.operatorService.exportOperators(dto, this.messageService)
            .then(data => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Success' });
                Utils.saveFileResponse(data, 'Operators' + moment(new Date()).format('DD MMM YYYY hh:mm:ss A'), dto.FileType);
                this.exportLoading = false;
            })
            .catch(err => {
                this.exportLoading = false;
                throw err;
            });
    }

    protected readonly OrderUtils = OrderUtils;
}
